/**
 * LU5 Flowchart Exercises — interactive widget embedded at the end of
 * Chapter 3 (Flowcharts) of StudyGuideLU5Gr10. Same five-tab framework
 * as LU5PseudocodeExercises; Phase 1 ships only Exercise 1 (Sequence
 * + shape recognition) with placeholders for the other four.
 *
 * Shape-and-pattern parallel to LU5PseudocodeExercises: the tuck-shop
 * scenario carries over from Ch 2 → Ch 3 so learners see the same
 * problem in two representations.
 *
 * Helpers (useLocalStorageState, FeedbackBox, Tag) intentionally
 * duplicated from the pseudocode widget rather than shared. Refactor
 * into a common module once N=3 widgets exist.
 */
import { useState, useEffect, ReactNode } from "react";
import { CheckCircle, XCircle, ChevronRight, Eye } from "lucide-react";

// ─── Tabs ─────────────────────────────────────────────────────────────────────

interface ExerciseTab {
  id: number;
  short: string;
}

const EXERCISES: ExerciseTab[] = [
  { id: 1, short: "Sequence" },
  { id: 2, short: "Selection" },
  { id: 3, short: "Spot the error" },
  { id: 4, short: "Fill in the blanks" },
  { id: 5, short: "Draw it yourself" },
];

const STORAGE_KEY = "lu5-flowchart-exercises-completed-v1";

// ─── localStorage hook ────────────────────────────────────────────────────────

function useLocalStorageState<T>(key: string, initial: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = window.localStorage.getItem(key);
      return stored ? (JSON.parse(stored) as T) : initial;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* ignore */
    }
  }, [key, value]);

  return [value, setValue];
}

// ─── Mini helpers ─────────────────────────────────────────────────────────────

function ScenarioBox({ children }: { children: ReactNode }) {
  return (
    <div className="border-l-4 border-amber-700 bg-amber-100/60 px-4 py-3 my-4 text-amber-950 text-sm">
      {children}
    </div>
  );
}

function FeedbackBox({ ok, children }: { ok: boolean; children: ReactNode }) {
  const cls = ok
    ? "border-emerald-600 bg-emerald-50 text-emerald-900"
    : "border-rose-600 bg-rose-50 text-rose-900";
  const Icon = ok ? CheckCircle : XCircle;
  return (
    <div className={`flex items-start gap-2 border-l-4 ${cls} px-4 py-3 my-3 text-sm`}>
      <Icon className="w-5 h-5 shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );
}

function Tag({ children }: { children: ReactNode }) {
  return (
    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-amber-200 text-amber-900 border border-amber-300">
      {children}
    </span>
  );
}

// ─── Reusable flowchart-shape primitives (SVG) ────────────────────────────────

const STROKE = "#78350f";        // amber-900
const FILL = "#fef3c7";          // amber-100
const FILL_BAD = "#fee2e2";      // rose-100 — for highlighting the wrong shape on Option C
const STROKE_BAD = "#b91c1c";    // rose-700

interface ShapeProps {
  cx: number;
  cy: number;
  label: string;
  flagged?: boolean;
}

function Oval({ cx, cy, label }: ShapeProps) {
  return (
    <g>
      <ellipse cx={cx} cy={cy} rx={50} ry={15} fill={FILL} stroke={STROKE} strokeWidth={2} />
      <text x={cx} y={cy + 4} textAnchor="middle" fontSize={11} fill={STROKE} fontFamily="Georgia, serif">
        {label}
      </text>
    </g>
  );
}

function Parallelogram({ cx, cy, label, flagged }: ShapeProps) {
  const fill = flagged ? FILL_BAD : FILL;
  const stroke = flagged ? STROKE_BAD : STROKE;
  // Skewed parallelogram — wider, with /  slope on left and right edges
  const w = 110;
  const h = 30;
  const skew = 10;
  const left = cx - w / 2;
  const right = cx + w / 2;
  const top = cy - h / 2;
  const bottom = cy + h / 2;
  const points = `${left + skew},${top} ${right},${top} ${right - skew},${bottom} ${left},${bottom}`;
  return (
    <g>
      <polygon points={points} fill={fill} stroke={stroke} strokeWidth={2} />
      <text x={cx} y={cy + 4} textAnchor="middle" fontSize={11} fill={stroke} fontFamily="Georgia, serif">
        {label}
      </text>
    </g>
  );
}

function Rectangle({ cx, cy, label, flagged }: ShapeProps) {
  const fill = flagged ? FILL_BAD : FILL;
  const stroke = flagged ? STROKE_BAD : STROKE;
  const w = 110;
  const h = 30;
  return (
    <g>
      <rect x={cx - w / 2} y={cy - h / 2} width={w} height={h} fill={fill} stroke={stroke} strokeWidth={2} />
      <text x={cx} y={cy + 4} textAnchor="middle" fontSize={11} fill={stroke} fontFamily="Georgia, serif">
        {label}
      </text>
    </g>
  );
}

function Diamond({ cx, cy, label, flagged }: ShapeProps) {
  const fill = flagged ? FILL_BAD : FILL;
  const stroke = flagged ? STROKE_BAD : STROKE;
  const halfW = 50;
  const halfH = 30;
  const points = `${cx},${cy - halfH} ${cx + halfW},${cy} ${cx},${cy + halfH} ${cx - halfW},${cy}`;
  return (
    <g>
      <polygon points={points} fill={fill} stroke={stroke} strokeWidth={2} />
      <text x={cx} y={cy + 4} textAnchor="middle" fontSize={11} fill={stroke} fontFamily="Georgia, serif">
        {label}
      </text>
    </g>
  );
}

function YesNoLabel({ x, y, text, flagged }: { x: number; y: number; text: string; flagged?: boolean }) {
  return (
    <text x={x} y={y} fontSize={10} fill={flagged ? STROKE_BAD : STROKE} fontFamily="Georgia, serif">
      {text}
    </text>
  );
}

function Arrow({ x1, y1, x2, y2 }: { x1: number; y1: number; x2: number; y2: number }) {
  return (
    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={STROKE} strokeWidth={2} markerEnd="url(#fc-arrow)" />
  );
}

function ArrowHead() {
  // Defs placed once per SVG.
  return (
    <defs>
      <marker id="fc-arrow" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={6} markerHeight={6} orient="auto">
        <path d="M 0 0 L 10 5 L 0 10 z" fill={STROKE} />
      </marker>
    </defs>
  );
}

// ─── Exercise 1 — three flowchart options for the tuck-shop scenario ──────────

function FlowchartOption({
  variant,
}: {
  variant: "A" | "B" | "C";
}) {
  // A — correct: parallelograms for I/O, rectangle for process, sequence is right.
  // B — sequence error: shapes are right but `total = …` runs BEFORE inputs.
  // C — shape error: sequence is right but inputs use Rectangle (process) instead
  //     of Parallelogram (I/O). Wrong-shape boxes are flagged in red so the
  //     learner can SEE the mistake when they look back at it.

  // y positions of the 6 boxes — same on every variant for clean comparison.
  const ys = [22, 70, 118, 166, 214, 262];

  return (
    <svg viewBox="0 0 240 290" className="w-full max-w-[240px] mx-auto">
      <ArrowHead />

      {/* Arrows between every consecutive pair */}
      {ys.slice(0, -1).map((y, i) => (
        <Arrow key={i} x1={120} y1={y + 15} x2={120} y2={ys[i + 1] - 15} />
      ))}

      {/* START */}
      <Oval cx={120} cy={ys[0]} label="START" />

      {/* Steps 2–5 vary by variant */}
      {variant === "A" && (
        <>
          <Parallelogram cx={120} cy={ys[1]} label="INPUT price" />
          <Parallelogram cx={120} cy={ys[2]} label="INPUT quantity" />
          <Rectangle cx={120} cy={ys[3]} label="total = price × quantity" />
          <Parallelogram cx={120} cy={ys[4]} label="DISPLAY total" />
        </>
      )}

      {variant === "B" && (
        <>
          <Rectangle cx={120} cy={ys[1]} label="total = price × quantity" />
          <Parallelogram cx={120} cy={ys[2]} label="INPUT price" />
          <Parallelogram cx={120} cy={ys[3]} label="INPUT quantity" />
          <Parallelogram cx={120} cy={ys[4]} label="DISPLAY total" />
        </>
      )}

      {variant === "C" && (
        <>
          <Rectangle cx={120} cy={ys[1]} label="INPUT price" flagged />
          <Rectangle cx={120} cy={ys[2]} label="INPUT quantity" flagged />
          <Rectangle cx={120} cy={ys[3]} label="total = price × quantity" />
          <Parallelogram cx={120} cy={ys[4]} label="DISPLAY total" />
        </>
      )}

      {/* END */}
      <Oval cx={120} cy={ys[5]} label="END" />
    </svg>
  );
}

const E1_OPTIONS: { letter: "A" | "B" | "C" }[] = [{ letter: "A" }, { letter: "B" }, { letter: "C" }];

const E1_CORRECT = "A";

const E1_FEEDBACK: Record<string, { ok: boolean; msg: string }> = {
  A: {
    ok: true,
    msg:
      "Correct. The shapes match their roles (parallelogram for input/output, rectangle for the calculation), and the order makes sense — read both values first, then calculate, then display.",
  },
  B: {
    ok: false,
    msg:
      "Look at the second shape from the top — total = price × quantity runs before price and quantity have been input. Same misconception as the pseudocode version: a flowchart still has to read values before it can use them. The shapes here are correct, but the sequence is wrong.",
  },
  C: {
    ok: false,
    msg:
      "The order is right, but the two highlighted shapes are wrong. Inputs and outputs use a parallelogram (the slanted shape), not a rectangle. A rectangle is for processing — calculations and assignments, not reading user input.",
  },
};

// ─── Exercise 1 — main component ──────────────────────────────────────────────

interface ExerciseProps {
  done: boolean;
  onComplete: () => void;
}

function Exercise1Sequence({ done, onComplete }: ExerciseProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [partBText, setPartBText] = useState("");
  const [partBRevealed, setPartBRevealed] = useState(false);

  const partACorrect = submitted && selected === E1_CORRECT;

  const handleCheck = () => {
    if (!selected) return;
    setSubmitted(true);
    if (selected === E1_CORRECT && partBRevealed && !done) {
      onComplete();
    }
  };

  const handleReveal = () => {
    setPartBRevealed(true);
    if (partACorrect && !done) {
      onComplete();
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <h3 className="text-xl font-bold text-amber-950" style={{ fontFamily: "Palatino, serif" }}>
          Exercise 1 — Sequence
        </h3>
        <Tag>Sequence + shapes</Tag>
      </div>

      <ScenarioBox>
        Same tuck-shop problem from Chapter 2: a program asks a customer for the price of one item
        and how many they want, then calculates and displays the total cost. Below are three
        flowcharts attempting to solve it — only one is right.
      </ScenarioBox>

      <p className="font-semibold text-amber-950 mt-4 mb-2">
        Part A — Which flowchart correctly solves this? Choose one.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {E1_OPTIONS.map((opt) => {
          const isSelected = selected === opt.letter;
          const showFeedback = submitted && isSelected;
          const fb = E1_FEEDBACK[opt.letter];
          const ringClass = showFeedback
            ? fb.ok
              ? "ring-2 ring-emerald-500"
              : "ring-2 ring-rose-500"
            : isSelected
            ? "ring-2 ring-amber-500"
            : "ring-1 ring-amber-300/40 hover:ring-amber-500";
          return (
            <label
              key={opt.letter}
              className={`block cursor-pointer rounded-lg p-3 transition bg-white/40 ${ringClass}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="radio"
                  name="flowchart-e1-partA"
                  className="accent-amber-700"
                  checked={isSelected}
                  onChange={() => {
                    setSelected(opt.letter);
                    setSubmitted(false);
                  }}
                />
                <span className="font-bold text-amber-900">{opt.letter}</span>
              </div>
              <FlowchartOption variant={opt.letter} />
            </label>
          );
        })}
      </div>

      <button
        onClick={handleCheck}
        disabled={!selected}
        className="mt-4 px-4 py-2 rounded-md bg-amber-800 text-amber-50 text-sm font-semibold hover:bg-amber-900 transition disabled:bg-amber-300 disabled:cursor-not-allowed"
      >
        Check answer
      </button>

      {submitted && selected && (
        <FeedbackBox ok={E1_FEEDBACK[selected].ok}>{E1_FEEDBACK[selected].msg}</FeedbackBox>
      )}

      {/* Part B */}
      <div className="mt-8 pt-6 border-t border-amber-200">
        <p className="font-semibold text-amber-950 mb-2">Part B — Plan your own.</p>
        <p className="text-sm text-amber-900 mb-3">
          Now plan a flowchart for: <em>ask the user for their name and age, then display "Hello, [name]! You are [age] years old."</em>
          {" "}List the shapes and their order in the box below — e.g.{" "}
          <code className="bg-amber-100 px-1 rounded">Oval (START) → Parallelogram (DISPLAY "What is your name?") → …</code>
        </p>

        <textarea
          value={partBText}
          onChange={(e) => setPartBText(e.target.value)}
          rows={6}
          spellCheck={false}
          placeholder='Oval (START) → Parallelogram (DISPLAY "...") → Parallelogram (INPUT name) → ...'
          className="w-full rounded-md bg-amber-50 border border-amber-300 px-3 py-2 text-sm font-mono text-amber-950 focus:outline-none focus:ring-2 focus:ring-amber-500"
        />

        <div className="flex items-center gap-3 mt-3">
          <button
            onClick={handleReveal}
            className="px-4 py-2 rounded-md bg-amber-100 text-amber-900 text-sm font-semibold hover:bg-amber-200 border border-amber-300 transition flex items-center gap-1.5"
          >
            <Eye className="w-4 h-4" />
            {partBRevealed ? "Hide model answer" : "Show model answer"}
          </button>
          {!partBRevealed && (
            <span className="text-xs text-amber-800 italic">Try it first, then compare.</span>
          )}
        </div>

        {partBRevealed && (
          <div className="mt-3">
            <p className="text-xs text-amber-800 mb-2 italic">Model answer (one valid flowchart):</p>
            <div className="bg-white/40 rounded-lg p-3 ring-1 ring-amber-200">
              <ModelGreetingFlowchart />
            </div>
            <p className="text-xs text-amber-700 mt-2">
              Notice the prompt-then-input pattern: each <strong>INPUT</strong> is preceded by a{" "}
              <strong>DISPLAY</strong> that tells the user what to type. Both use the parallelogram
              (input/output) shape; only the final greeting is also output. The key is the order:
              prompt → input → prompt → input → output.
            </p>
          </div>
        )}
      </div>

      {done && (
        <div className="mt-6 flex items-center gap-2 text-emerald-700 text-sm font-semibold">
          <CheckCircle className="w-4 h-4" />
          Exercise complete.
        </div>
      )}
    </div>
  );
}

// ─── Model answer SVG for Part B ──────────────────────────────────────────────

function ModelGreetingFlowchart() {
  // 6 boxes: START → DISPLAY "What is your name?" → INPUT name →
  // DISPLAY "What is your age?" → INPUT age → DISPLAY "Hello, ..." → END
  // Note: "DISPLAY 'Hello, [name]!...'" is one parallelogram; END is one oval.
  // Total: 7 shapes.
  const ys = [22, 70, 118, 166, 214, 262, 310];
  return (
    <svg viewBox="0 0 280 340" className="w-full max-w-[280px] mx-auto">
      <ArrowHead />
      {ys.slice(0, -1).map((y, i) => (
        <Arrow key={i} x1={140} y1={y + 15} x2={140} y2={ys[i + 1] - 15} />
      ))}
      <Oval cx={140} cy={ys[0]} label="START" />
      <Parallelogram cx={140} cy={ys[1]} label='DISPLAY "name?"' />
      <Parallelogram cx={140} cy={ys[2]} label="INPUT name" />
      <Parallelogram cx={140} cy={ys[3]} label='DISPLAY "age?"' />
      <Parallelogram cx={140} cy={ys[4]} label="INPUT age" />
      <Parallelogram cx={140} cy={ys[5]} label='DISPLAY greeting' />
      <Oval cx={140} cy={ys[6]} label="END" />
    </svg>
  );
}

// ─── Exercise 2 — Selection (IF / ELSE) ───────────────────────────────────────
//
// SAG focus: "IF condition THEN ... ELSE ... END IF" — basic two-way decision.
// Scenario: a gym charges R200/month; members under 18 get a 25% discount.
// Three flowchart options test three things students get wrong:
//   A — correct: condition matches intent, Yes/No on the right tips, branches
//       merge before the single output.
//   B — Yes/No labels swapped on the diamond. Branches still apply the same
//       processes, but now the "Yes (under-18)" branch lands on full price and
//       the "No" branch on the discount. The labels are flagged so the
//       learner can see WHERE the bug is on review.
//   C — branches don't merge: each one has its own DISPLAY + END. Two ENDs
//       in one flowchart — common beginner mistake. The duplicated END is
//       flagged.

function FlowchartOptionExercise2({ variant }: { variant: "A" | "B" | "C" }) {
  // Layout (shared across A, B, C for visual comparison):
  //   y = 22    START oval, cx = 140
  //   y = 70    INPUT age parallelogram
  //   y = 130   Diamond "age < 18?" (top 100, bottom 160, left 90, right 190)
  //   y = 200   Two rectangles, left at cx=60, right at cx=220
  //   y = 280   DISPLAY fee parallelogram (or two for variant C)
  //   y = 330   END oval (or two for variant C)

  const isC = variant === "C";

  return (
    <svg viewBox="0 0 280 360" className="w-full max-w-[280px] mx-auto">
      <ArrowHead />

      {/* START → INPUT age → Diamond top */}
      <Oval cx={140} cy={22} label="START" />
      <Arrow x1={140} y1={37} x2={140} y2={55} />
      <Parallelogram cx={140} cy={70} label="INPUT age" />
      <Arrow x1={140} y1={85} x2={140} y2={100} />

      <Diamond cx={140} cy={130} label="age < 18 ?" />

      {/* Yes/No labels — placement depends on variant */}
      {variant === "A" && (
        <>
          <YesNoLabel x={48} y={125} text="Yes" />
          <YesNoLabel x={196} y={125} text="No" />
        </>
      )}
      {variant === "B" && (
        <>
          {/* Labels are swapped relative to the diamond's actual logic */}
          <YesNoLabel x={48} y={125} text="No" flagged />
          <YesNoLabel x={196} y={125} text="Yes" flagged />
        </>
      )}
      {variant === "C" && (
        <>
          <YesNoLabel x={48} y={125} text="Yes" />
          <YesNoLabel x={196} y={125} text="No" />
        </>
      )}

      {/* Diamond left tip → left rectangle (discount) */}
      <line x1={90} y1={130} x2={60} y2={130} stroke={STROKE} strokeWidth={2} />
      <Arrow x1={60} y1={130} x2={60} y2={185} />
      <Rectangle cx={60} cy={200} label="fee = 200 × 0.75" />

      {/* Diamond right tip → right rectangle (full) */}
      <line x1={190} y1={130} x2={220} y2={130} stroke={STROKE} strokeWidth={2} />
      <Arrow x1={220} y1={130} x2={220} y2={185} />
      <Rectangle cx={220} cy={200} label="fee = 200" />

      {!isC && (
        <>
          {/* Branches merge at (140, 245) → DISPLAY → END */}
          <line x1={60} y1={215} x2={60} y2={245} stroke={STROKE} strokeWidth={2} />
          <line x1={220} y1={215} x2={220} y2={245} stroke={STROKE} strokeWidth={2} />
          <line x1={60} y1={245} x2={220} y2={245} stroke={STROKE} strokeWidth={2} />
          <Arrow x1={140} y1={245} x2={140} y2={265} />
          <Parallelogram cx={140} cy={280} label="DISPLAY fee" />
          <Arrow x1={140} y1={295} x2={140} y2={315} />
          <Oval cx={140} cy={330} label="END" />
        </>
      )}

      {isC && (
        <>
          {/* No merge — each branch has its own DISPLAY + END (the END oval
              is the misconception; flag it red on each branch). */}
          <Arrow x1={60} y1={215} x2={60} y2={260} />
          <Parallelogram cx={60} cy={275} label="DISPLAY fee" />
          <Arrow x1={60} y1={290} x2={60} y2={315} />
          <Oval cx={60} cy={330} label="END" />

          <Arrow x1={220} y1={215} x2={220} y2={260} />
          <Parallelogram cx={220} cy={275} label="DISPLAY fee" />
          <Arrow x1={220} y1={290} x2={220} y2={315} />
          {/* Re-render the duplicate END flagged. Reuse Oval but draw a
              second visual hint by overlaying a red ring. */}
          <ellipse
            cx={220}
            cy={330}
            rx={50}
            ry={15}
            fill={FILL_BAD}
            stroke={STROKE_BAD}
            strokeWidth={2}
          />
          <text
            x={220}
            y={334}
            textAnchor="middle"
            fontSize={11}
            fill={STROKE_BAD}
            fontFamily="Georgia, serif"
          >
            END
          </text>
        </>
      )}
    </svg>
  );
}

const E2_OPTIONS: { letter: "A" | "B" | "C" }[] = [{ letter: "A" }, { letter: "B" }, { letter: "C" }];

const E2_CORRECT = "A";

const E2_FEEDBACK: Record<string, { ok: boolean; msg: string }> = {
  A: {
    ok: true,
    msg:
      "Correct. The condition (age < 18?) matches the intent, Yes goes to the discount rectangle, No goes to full price, and both branches merge before the single DISPLAY + END. That's a textbook two-way IF / ELSE in flowchart form.",
  },
  B: {
    ok: false,
    msg:
      "The shapes and structure are right, but look at the Yes / No labels on the diamond — they're on the wrong tips. With these labels, an under-18 (Yes branch) would end up on the right rectangle (fee = 200, full price) and an over-18 (No branch) would get the discount. The condition reads correctly but the labels reverse the logic.",
  },
  C: {
    ok: false,
    msg:
      "The two branches each have their own DISPLAY and their own END. In a flowchart with one IF / ELSE, the two paths should rejoin and share a single output and a single END oval. Two ENDs is a strong sign that the merge step is missing — and it makes the flowchart harder to read once the program grows.",
  },
};

function Exercise2Selection({ done, onComplete }: ExerciseProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [partBText, setPartBText] = useState("");
  const [partBRevealed, setPartBRevealed] = useState(false);

  const partACorrect = submitted && selected === E2_CORRECT;

  const handleCheck = () => {
    if (!selected) return;
    setSubmitted(true);
    if (selected === E2_CORRECT && partBRevealed && !done) {
      onComplete();
    }
  };

  const handleReveal = () => {
    setPartBRevealed(true);
    if (partACorrect && !done) {
      onComplete();
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <h3 className="text-xl font-bold text-amber-950" style={{ fontFamily: "Palatino, serif" }}>
          Exercise 2 — Selection
        </h3>
        <Tag>IF / ELSE</Tag>
      </div>

      <ScenarioBox>
        A gym charges <strong>R200 per month</strong>. Members <strong>under 18</strong> get a 25 %
        discount. The program reads the member's age and displays the correct fee. Three flowcharts
        attempt this — only one is right.
      </ScenarioBox>

      <p className="font-semibold text-amber-950 mt-4 mb-2">
        Part A — Which flowchart correctly solves this? Choose one.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {E2_OPTIONS.map((opt) => {
          const isSelected = selected === opt.letter;
          const showFeedback = submitted && isSelected;
          const fb = E2_FEEDBACK[opt.letter];
          const ringClass = showFeedback
            ? fb.ok
              ? "ring-2 ring-emerald-500"
              : "ring-2 ring-rose-500"
            : isSelected
            ? "ring-2 ring-amber-500"
            : "ring-1 ring-amber-300/40 hover:ring-amber-500";
          return (
            <label
              key={opt.letter}
              className={`block cursor-pointer rounded-lg p-3 transition bg-white/40 ${ringClass}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="radio"
                  name="flowchart-e2-partA"
                  className="accent-amber-700"
                  checked={isSelected}
                  onChange={() => {
                    setSelected(opt.letter);
                    setSubmitted(false);
                  }}
                />
                <span className="font-bold text-amber-900">{opt.letter}</span>
              </div>
              <FlowchartOptionExercise2 variant={opt.letter} />
            </label>
          );
        })}
      </div>

      <button
        onClick={handleCheck}
        disabled={!selected}
        className="mt-4 px-4 py-2 rounded-md bg-amber-800 text-amber-50 text-sm font-semibold hover:bg-amber-900 transition disabled:bg-amber-300 disabled:cursor-not-allowed"
      >
        Check answer
      </button>

      {submitted && selected && (
        <FeedbackBox ok={E2_FEEDBACK[selected].ok}>{E2_FEEDBACK[selected].msg}</FeedbackBox>
      )}

      {/* Part B */}
      <div className="mt-8 pt-6 border-t border-amber-200">
        <p className="font-semibold text-amber-950 mb-2">Part B — Plan your own.</p>
        <p className="text-sm text-amber-900 mb-3">
          Now plan a flowchart for: <em>a movie ticket costs R80; children under 12 pay half price.
          The program reads the customer's age and displays the price.</em>
          {" "}List the shapes in order, and remember the diamond — e.g.{" "}
          <code className="bg-amber-100 px-1 rounded">Oval (START) → Parallelogram (INPUT age) → Diamond (age &lt; 12?) → ...</code>
        </p>

        <textarea
          value={partBText}
          onChange={(e) => setPartBText(e.target.value)}
          rows={6}
          spellCheck={false}
          placeholder="Oval (START) → Parallelogram (INPUT age) → Diamond (age < 12?) → Yes: ... → No: ... → merge → DISPLAY → END"
          className="w-full rounded-md bg-amber-50 border border-amber-300 px-3 py-2 text-sm font-mono text-amber-950 focus:outline-none focus:ring-2 focus:ring-amber-500"
        />

        <div className="flex items-center gap-3 mt-3">
          <button
            onClick={handleReveal}
            className="px-4 py-2 rounded-md bg-amber-100 text-amber-900 text-sm font-semibold hover:bg-amber-200 border border-amber-300 transition flex items-center gap-1.5"
          >
            <Eye className="w-4 h-4" />
            {partBRevealed ? "Hide model answer" : "Show model answer"}
          </button>
          {!partBRevealed && (
            <span className="text-xs text-amber-800 italic">Try it first, then compare.</span>
          )}
        </div>

        {partBRevealed && (
          <div className="mt-3">
            <p className="text-xs text-amber-800 mb-2 italic">Model answer (one valid flowchart):</p>
            <div className="bg-white/40 rounded-lg p-3 ring-1 ring-amber-200">
              <ModelMovieTicketFlowchart />
            </div>
            <p className="text-xs text-amber-700 mt-2">
              Same shape as the gym example: one decision diamond, two branches with one process
              each, both rejoin before a single DISPLAY and a single END. The condition{" "}
              <code className="bg-amber-100 px-1 rounded">age &lt; 12?</code> matches the intent —
              Yes goes to the half-price branch, No goes to the full-price branch.
            </p>
          </div>
        )}
      </div>

      {done && (
        <div className="mt-6 flex items-center gap-2 text-emerald-700 text-sm font-semibold">
          <CheckCircle className="w-4 h-4" />
          Exercise complete.
        </div>
      )}
    </div>
  );
}

// ─── Model answer for Exercise 2 Part B ───────────────────────────────────────

function ModelMovieTicketFlowchart() {
  // Same shape as Option A of Exercise 2; different scenario.
  return (
    <svg viewBox="0 0 280 360" className="w-full max-w-[280px] mx-auto">
      <ArrowHead />
      <Oval cx={140} cy={22} label="START" />
      <Arrow x1={140} y1={37} x2={140} y2={55} />
      <Parallelogram cx={140} cy={70} label="INPUT age" />
      <Arrow x1={140} y1={85} x2={140} y2={100} />

      <Diamond cx={140} cy={130} label="age < 12 ?" />
      <YesNoLabel x={48} y={125} text="Yes" />
      <YesNoLabel x={196} y={125} text="No" />

      <line x1={90} y1={130} x2={60} y2={130} stroke={STROKE} strokeWidth={2} />
      <Arrow x1={60} y1={130} x2={60} y2={185} />
      <Rectangle cx={60} cy={200} label="price = 80 × 0.5" />

      <line x1={190} y1={130} x2={220} y2={130} stroke={STROKE} strokeWidth={2} />
      <Arrow x1={220} y1={130} x2={220} y2={185} />
      <Rectangle cx={220} cy={200} label="price = 80" />

      <line x1={60} y1={215} x2={60} y2={245} stroke={STROKE} strokeWidth={2} />
      <line x1={220} y1={215} x2={220} y2={245} stroke={STROKE} strokeWidth={2} />
      <line x1={60} y1={245} x2={220} y2={245} stroke={STROKE} strokeWidth={2} />
      <Arrow x1={140} y1={245} x2={140} y2={265} />
      <Parallelogram cx={140} cy={280} label="DISPLAY price" />
      <Arrow x1={140} y1={295} x2={140} y2={315} />
      <Oval cx={140} cy={330} label="END" />
    </svg>
  );
}

// ─── Exercise 3 — Spot the error ──────────────────────────────────────────────
//
// Show ONE buggy counting-loop flowchart. Single-select MCQ: which error is
// present? Three of the four options are deliberate distractors that look
// like errors but aren't.
//
// The bug we render: the loop body is missing the `count = count + 1`
// increment shape. The loop-back arrow goes straight from DISPLAY back into
// the diamond, so `count` stays at 1 forever — infinite loop.

function BrokenCountingLoopFlowchart() {
  // viewBox 0 0 320 380; main column at x=140; right column at x=260 for END.
  return (
    <svg viewBox="0 0 320 380" className="w-full max-w-[320px] mx-auto">
      <ArrowHead />

      <Oval cx={140} cy={22} label="START" />
      <Arrow x1={140} y1={37} x2={140} y2={55} />
      <Rectangle cx={140} cy={70} label="count = 1" />
      <Arrow x1={140} y1={85} x2={140} y2={100} />

      <Diamond cx={140} cy={130} label="count <= 5 ?" />
      <YesNoLabel x={148} y={172} text="Yes" />
      <YesNoLabel x={196} y={125} text="No" />

      {/* Yes path goes down → DISPLAY count → loop back to diamond (no increment!) */}
      <Arrow x1={140} y1={160} x2={140} y2={185} />
      <Parallelogram cx={140} cy={200} label="DISPLAY count" />

      {/* The buggy loop-back: from DISPLAY's bottom-left, around the left side,
          back into the diamond's left tip. No increment shape in between. */}
      <line x1={85} y1={215} x2={45} y2={215} stroke={STROKE} strokeWidth={2} />
      <line x1={45} y1={215} x2={45} y2={130} stroke={STROKE} strokeWidth={2} />
      <line x1={45} y1={130} x2={88} y2={130} stroke={STROKE} strokeWidth={2} markerEnd="url(#fc-arrow)" />

      {/* No path goes right → END */}
      <line x1={190} y1={130} x2={260} y2={130} stroke={STROKE} strokeWidth={2} />
      <line x1={260} y1={130} x2={260} y2={300} stroke={STROKE} strokeWidth={2} markerEnd="url(#fc-arrow)" />
      <Oval cx={260} cy={320} label="END" />
    </svg>
  );
}

const E3_OPTIONS = [
  {
    letter: "A",
    text: "DISPLAY should be OUTPUT — that's the only valid keyword.",
  },
  {
    letter: "B",
    text: "The diamond is the wrong shape — decisions should use a parallelogram.",
  },
  {
    letter: "C",
    text:
      "There's no `count = count + 1` step. count stays at 1 forever, so the loop never ends.",
  },
  {
    letter: "D",
    text: "The arrows have no direction — flowchart arrows must always have an arrowhead.",
  },
];

const E3_CORRECT = "C";

const E3_FEEDBACK: Record<string, { ok: boolean; msg: string }> = {
  A: {
    ok: false,
    msg:
      "DISPLAY is fine — IEB pseudocode and most textbooks accept both DISPLAY and OUTPUT for putting a value on the screen. They're synonyms; using either won't lose marks.",
  },
  B: {
    ok: false,
    msg:
      "Diamond IS the right shape for a decision — that's exactly what the four flowchart shapes are for (oval, parallelogram, rectangle, diamond). The diamond here is doing its job.",
  },
  C: {
    ok: true,
    msg:
      "Correct. Trace it: count starts at 1, the diamond checks count <= 5 (true), DISPLAY 1 runs, the arrow loops straight back to the diamond — but count was never updated. count is still 1, the condition is still true, DISPLAY 1 runs again. Forever. Every loop must include something inside the body that eventually makes the condition false. Here that means a `count = count + 1` rectangle between DISPLAY and the loop-back arrow.",
  },
  D: {
    ok: false,
    msg:
      "Look at the arrows again — every line in this flowchart has an arrowhead at its end. The arrowheads are small, but they're there. Direction is set correctly.",
  },
};

function Exercise3SpotError({ done, onComplete }: ExerciseProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleCheck = () => {
    if (!selected) return;
    setSubmitted(true);
    if (selected === E3_CORRECT && !done) {
      onComplete();
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <h3 className="text-xl font-bold text-amber-950" style={{ fontFamily: "Palatino, serif" }}>
          Exercise 3 — Spot the error
        </h3>
        <Tag>Iteration / loop bug</Tag>
      </div>

      <ScenarioBox>
        Below is a flowchart that's <strong>supposed to</strong> print the numbers 1 to 5. Read it
        carefully — it has <strong>one</strong> bug. The other three options are red herrings.
      </ScenarioBox>

      <div className="my-4 bg-white/40 rounded-lg p-3 ring-1 ring-amber-200 max-w-md mx-auto">
        <BrokenCountingLoopFlowchart />
      </div>

      <p className="font-semibold text-amber-950 mt-4 mb-2">
        Part A — What's wrong with this flowchart?
      </p>

      <div className="space-y-2">
        {E3_OPTIONS.map((opt) => {
          const isSelected = selected === opt.letter;
          const showFeedback = submitted && isSelected;
          const fb = E3_FEEDBACK[opt.letter];
          const ringClass = showFeedback
            ? fb.ok
              ? "ring-2 ring-emerald-500"
              : "ring-2 ring-rose-500"
            : isSelected
            ? "ring-2 ring-amber-500"
            : "ring-1 ring-amber-300/40 hover:ring-amber-500";
          return (
            <label
              key={opt.letter}
              className={`flex items-start gap-3 cursor-pointer rounded-lg p-3 transition bg-white/40 ${ringClass}`}
            >
              <input
                type="radio"
                name="flowchart-e3"
                className="mt-1 accent-amber-700"
                checked={isSelected}
                onChange={() => {
                  setSelected(opt.letter);
                  setSubmitted(false);
                }}
              />
              <span className="text-sm text-amber-950">
                <strong className="text-amber-900">{opt.letter}.</strong> {opt.text}
              </span>
            </label>
          );
        })}
      </div>

      <button
        onClick={handleCheck}
        disabled={!selected}
        className="mt-4 px-4 py-2 rounded-md bg-amber-800 text-amber-50 text-sm font-semibold hover:bg-amber-900 transition disabled:bg-amber-300 disabled:cursor-not-allowed"
      >
        Check answer
      </button>

      {submitted && selected && (
        <FeedbackBox ok={E3_FEEDBACK[selected].ok}>{E3_FEEDBACK[selected].msg}</FeedbackBox>
      )}

      {done && (
        <div className="mt-6 flex items-center gap-2 text-emerald-700 text-sm font-semibold">
          <CheckCircle className="w-4 h-4" />
          Exercise complete.
        </div>
      )}
    </div>
  );
}

// ─── Exercise 4 — Fill in the blanks ──────────────────────────────────────────
//
// Show a counting-loop flowchart with three blanks (numbered ① ② ③).
// Below: three input fields. Check button validates each — green if right,
// red with the expected answer revealed if wrong.

function CountingLoopFlowchartWithBlanks({
  showAnswers,
  results,
}: {
  showAnswers: boolean;
  results: { 1?: boolean; 2?: boolean; 3?: boolean };
}) {
  // Resolved or blank text per slot.
  const slot1 = showAnswers && results[1] === false ? "<=" : "①";
  const slot2 = showAnswers && results[2] === false ? "DISPLAY" : "②";
  const slot3 = showAnswers && results[3] === false ? "+" : "③";

  return (
    <svg viewBox="0 0 320 480" className="w-full max-w-[320px] mx-auto">
      <ArrowHead />

      <Oval cx={140} cy={22} label="START" />
      <Arrow x1={140} y1={37} x2={140} y2={55} />
      <Rectangle cx={140} cy={70} label="count = 1" />
      <Arrow x1={140} y1={85} x2={140} y2={100} />

      <Diamond cx={140} cy={130} label={`count ${slot1} 5 ?`} />
      <YesNoLabel x={148} y={172} text="Yes" />
      <YesNoLabel x={196} y={125} text="No" />

      <Arrow x1={140} y1={160} x2={140} y2={185} />
      <Parallelogram cx={140} cy={200} label={`${slot2} count`} />
      <Arrow x1={140} y1={215} x2={140} y2={245} />

      <Rectangle cx={140} cy={260} label={`count = count ${slot3} 1`} />

      {/* Loop-back from bottom of update rect, around the left, into the diamond's left tip */}
      <line x1={85} y1={275} x2={45} y2={275} stroke={STROKE} strokeWidth={2} />
      <line x1={45} y1={275} x2={45} y2={130} stroke={STROKE} strokeWidth={2} />
      <line x1={45} y1={130} x2={88} y2={130} stroke={STROKE} strokeWidth={2} markerEnd="url(#fc-arrow)" />

      {/* No path → END */}
      <line x1={190} y1={130} x2={260} y2={130} stroke={STROKE} strokeWidth={2} />
      <line x1={260} y1={130} x2={260} y2={400} stroke={STROKE} strokeWidth={2} markerEnd="url(#fc-arrow)" />
      <Oval cx={260} cy={420} label="END" />
    </svg>
  );
}

interface BlankSpec {
  hint: string;
  accept: string[]; // case-insensitive, trimmed
  display: string;  // shown if learner gets it wrong
}

const E4_BLANKS: Record<"1" | "2" | "3", BlankSpec> = {
  "1": {
    hint: "Operator inside the diamond — count needs to keep going while it's still 5 or below.",
    accept: ["<=", "<= "],
    display: "<=",
  },
  "2": {
    hint: "Keyword that puts a value on the screen.",
    accept: ["display", "output"],
    display: "DISPLAY",
  },
  "3": {
    hint: "Operator that increases count by 1 each time round the loop.",
    accept: ["+"],
    display: "+",
  },
};

function checkBlank(slot: "1" | "2" | "3", value: string): boolean {
  const normalised = value.trim().toLowerCase();
  return E4_BLANKS[slot].accept.includes(normalised);
}

function Exercise4FillBlanks({ done, onComplete }: ExerciseProps) {
  const [v1, setV1] = useState("");
  const [v2, setV2] = useState("");
  const [v3, setV3] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const result1 = submitted ? checkBlank("1", v1) : undefined;
  const result2 = submitted ? checkBlank("2", v2) : undefined;
  const result3 = submitted ? checkBlank("3", v3) : undefined;
  const allCorrect = !!result1 && !!result2 && !!result3;

  const handleCheck = () => {
    setSubmitted(true);
    const ok = checkBlank("1", v1) && checkBlank("2", v2) && checkBlank("3", v3);
    if (ok && !done) {
      onComplete();
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <h3 className="text-xl font-bold text-amber-950" style={{ fontFamily: "Palatino, serif" }}>
          Exercise 4 — Fill in the blanks
        </h3>
        <Tag>Counting loop</Tag>
      </div>

      <ScenarioBox>
        This flowchart prints the numbers 1 to 5. Three pieces are blanked out — labelled{" "}
        <strong>①</strong>, <strong>②</strong>, <strong>③</strong>. Type the missing piece into
        each input below and click Check.
      </ScenarioBox>

      <div className="my-4 bg-white/40 rounded-lg p-3 ring-1 ring-amber-200 max-w-md mx-auto">
        <CountingLoopFlowchartWithBlanks
          showAnswers={submitted}
          results={{ 1: result1, 2: result2, 3: result3 }}
        />
      </div>

      <div className="space-y-3 mt-4">
        {(["1", "2", "3"] as const).map((slot) => {
          const value = slot === "1" ? v1 : slot === "2" ? v2 : v3;
          const setter = slot === "1" ? setV1 : slot === "2" ? setV2 : setV3;
          const result = slot === "1" ? result1 : slot === "2" ? result2 : result3;
          const spec = E4_BLANKS[slot];
          const ringClass =
            result === undefined
              ? "ring-1 ring-amber-300/40 focus-within:ring-amber-500"
              : result
              ? "ring-2 ring-emerald-500"
              : "ring-2 ring-rose-500";
          return (
            <div
              key={slot}
              className={`flex items-start gap-3 rounded-lg p-3 bg-white/40 ${ringClass}`}
            >
              <span className="font-bold text-amber-900 text-lg leading-none mt-0.5">
                {slot === "1" ? "①" : slot === "2" ? "②" : "③"}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-amber-900 mb-1">{spec.hint}</p>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => {
                    setter(e.target.value);
                    setSubmitted(false);
                  }}
                  spellCheck={false}
                  className="w-full max-w-[180px] rounded-md bg-amber-50 border border-amber-300 px-2 py-1 text-sm font-mono text-amber-950 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                {result === false && (
                  <p className="text-xs text-rose-700 mt-1">
                    Expected: <code className="bg-rose-100 px-1 rounded font-mono">{spec.display}</code>
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={handleCheck}
        disabled={!v1 && !v2 && !v3}
        className="mt-4 px-4 py-2 rounded-md bg-amber-800 text-amber-50 text-sm font-semibold hover:bg-amber-900 transition disabled:bg-amber-300 disabled:cursor-not-allowed"
      >
        Check answer
      </button>

      {submitted && (
        <FeedbackBox ok={allCorrect}>
          {allCorrect ? (
            <>All three blanks correct. The loop runs while <code>count &lt;= 5</code>, displays the
            value, and adds 1 each iteration — the standard counting-loop pattern.</>
          ) : (
            <>
              Some blanks aren't right yet — the expected answer is shown next to each. Review the
              hint and try again. Tip: the operator in slot ① has to let count REACH 5, not stop
              short of it.
            </>
          )}
        </FeedbackBox>
      )}

      {done && (
        <div className="mt-6 flex items-center gap-2 text-emerald-700 text-sm font-semibold">
          <CheckCircle className="w-4 h-4" />
          Exercise complete.
        </div>
      )}
    </div>
  );
}

// ─── Exercise 5 — Draw it yourself ────────────────────────────────────────────
//
// Capstone: combine all three control structures (sequence + iteration +
// selection) plus a counter pattern in one flowchart. Free-text plan + self-
// assessment checklist + model-answer reveal with full SVG.
// Mark complete when the model is revealed (matches Ex 1 / Ex 2 pattern).

const E5_CHECKLIST = [
  "I have START and END ovals (one of each).",
  "I initialise a pass counter (e.g. passCount = 0) BEFORE the loop.",
  "I initialise a loop counter (e.g. i = 1) BEFORE the loop.",
  "My loop uses a diamond to check the loop condition (e.g. i <= 5).",
  "Inside the loop, I have a parallelogram to INPUT each mark.",
  "Inside the loop, I have a diamond that checks if mark >= 50.",
  "I increment passCount only on the Yes branch of the mark-check diamond.",
  "I increment the loop counter (i = i + 1) inside the loop, before the loop-back.",
  "The DISPLAY for passCount is AFTER the loop ends, on the No branch of the loop diamond.",
];

function Exercise5DrawYourself({ done, onComplete }: ExerciseProps) {
  const [text, setText] = useState("");
  const [checks, setChecks] = useState<boolean[]>(() => E5_CHECKLIST.map(() => false));
  const [revealed, setRevealed] = useState(false);

  const handleReveal = () => {
    setRevealed(true);
    if (!done) onComplete();
  };

  const toggleCheck = (i: number) => {
    setChecks((prev) => prev.map((v, idx) => (idx === i ? !v : v)));
  };

  const checkedCount = checks.filter(Boolean).length;

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <h3 className="text-xl font-bold text-amber-950" style={{ fontFamily: "Palatino, serif" }}>
          Exercise 5 — Draw it yourself
        </h3>
        <Tag>Capstone</Tag>
      </div>

      <ScenarioBox>
        Plan a flowchart that <strong>reads 5 marks</strong>, counts how many are{" "}
        <strong>50 or higher</strong> (passes), and then <strong>displays the pass count</strong>.
        This combines all three control structures: sequence (the steps in order), iteration (the
        loop over 5 marks), and selection (the pass/fail check). Plus an accumulator (passCount).
      </ScenarioBox>

      <p className="font-semibold text-amber-950 mt-4 mb-2">Part A — Sketch your plan.</p>
      <p className="text-sm text-amber-900 mb-3">
        Describe the flowchart as a list of shapes in order. Aim for the structure first; exact
        wording can vary.
      </p>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={10}
        spellCheck={false}
        placeholder={`Oval (START)\nRectangle (passCount = 0)\nRectangle (i = 1)\nDiamond (i <= 5 ?)\n  Yes:\n    Parallelogram (INPUT mark)\n    Diamond (mark >= 50 ?)\n      Yes: Rectangle (passCount = passCount + 1)\n      No: (skip)\n    merge\n    Rectangle (i = i + 1)\n    loop back to first diamond\n  No:\n    Parallelogram (DISPLAY passCount)\n    Oval (END)`}
        className="w-full rounded-md bg-amber-50 border border-amber-300 px-3 py-2 text-sm font-mono text-amber-950 focus:outline-none focus:ring-2 focus:ring-amber-500"
      />

      <p className="font-semibold text-amber-950 mt-6 mb-2">
        Part B — Self-assessment checklist
      </p>
      <p className="text-sm text-amber-900 mb-3">
        Tick each item as you verify your plan covers it.{" "}
        <strong>{checkedCount} of {E5_CHECKLIST.length} ticked.</strong>
      </p>

      <div className="space-y-2">
        {E5_CHECKLIST.map((item, i) => (
          <label
            key={i}
            className="flex items-start gap-3 cursor-pointer rounded-lg p-2 hover:bg-amber-100/50 transition"
          >
            <input
              type="checkbox"
              className="mt-1 accent-amber-700"
              checked={checks[i]}
              onChange={() => toggleCheck(i)}
            />
            <span className="text-sm text-amber-950">{item}</span>
          </label>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-6">
        <button
          onClick={handleReveal}
          className="px-4 py-2 rounded-md bg-amber-100 text-amber-900 text-sm font-semibold hover:bg-amber-200 border border-amber-300 transition flex items-center gap-1.5"
        >
          <Eye className="w-4 h-4" />
          {revealed ? "Hide model answer" : "Show model answer"}
        </button>
        {!revealed && (
          <span className="text-xs text-amber-800 italic">
            Try the plan + checklist first, then compare.
          </span>
        )}
      </div>

      {revealed && (
        <div className="mt-3">
          <p className="text-xs text-amber-800 mb-2 italic">Model answer (one valid flowchart):</p>
          <div className="bg-white/40 rounded-lg p-3 ring-1 ring-amber-200 overflow-x-auto">
            <ModelPassCountFlowchart />
          </div>
          <p className="text-xs text-amber-700 mt-2">
            Two diamonds: the outer one controls the loop (5 iterations), the inner one decides
            whether to bump the pass counter. The Yes branch of the inner diamond does the bump;
            the No branch skips it; both rejoin before the loop counter is incremented and we go
            back to the outer diamond. When the loop counter hits 6, the outer diamond's No branch
            takes us out to DISPLAY passCount and END.
          </p>
        </div>
      )}

      {done && (
        <div className="mt-6 flex items-center gap-2 text-emerald-700 text-sm font-semibold">
          <CheckCircle className="w-4 h-4" />
          Exercise complete.
        </div>
      )}
    </div>
  );
}

// ─── Model answer SVG for Exercise 5 ──────────────────────────────────────────

function ModelPassCountFlowchart() {
  // viewBox 0 0 480 720
  // Main column at x=240. Right column at x=400 for the No-exit (DISPLAY+END).
  // Increment branch on the LEFT at x=80 for `passCount = passCount + 1`.
  // Loop-back arrow goes around the far LEFT side from i=i+1 back to outer
  // diamond.
  return (
    <svg viewBox="0 0 480 720" className="w-full max-w-[480px] mx-auto">
      <ArrowHead />

      <Oval cx={240} cy={22} label="START" />
      <Arrow x1={240} y1={37} x2={240} y2={55} />
      <Rectangle cx={240} cy={70} label="passCount = 0" />
      <Arrow x1={240} y1={85} x2={240} y2={115} />
      <Rectangle cx={240} cy={130} label="i = 1" />
      <Arrow x1={240} y1={145} x2={240} y2={170} />

      {/* Outer (loop) diamond */}
      <Diamond cx={240} cy={200} label="i <= 5 ?" />
      <YesNoLabel x={248} y={242} text="Yes" />
      <YesNoLabel x={296} y={195} text="No" />

      {/* No branch goes RIGHT to the right column → DISPLAY → END */}
      <line x1={290} y1={200} x2={400} y2={200} stroke={STROKE} strokeWidth={2} markerEnd="url(#fc-arrow)" />
      <Parallelogram cx={400} cy={240} label="DISPLAY passCount" />
      <Arrow x1={400} y1={255} x2={400} y2={285} />
      <Oval cx={400} cy={300} label="END" />

      {/* Yes branch goes DOWN — INPUT mark */}
      <Arrow x1={240} y1={230} x2={240} y2={260} />
      <Parallelogram cx={240} cy={275} label="INPUT mark" />
      <Arrow x1={240} y1={290} x2={240} y2={325} />

      {/* Inner (mark) diamond */}
      <Diamond cx={240} cy={355} label="mark >= 50 ?" />
      <YesNoLabel x={148} y={350} text="Yes" />
      <YesNoLabel x={248} y={397} text="No" />

      {/* Yes branch goes LEFT to passCount = passCount + 1 */}
      <line x1={190} y1={355} x2={80} y2={355} stroke={STROKE} strokeWidth={2} />
      <Arrow x1={80} y1={355} x2={80} y2={420} />
      <Rectangle cx={80} cy={435} label="passCount + 1" />
      <line x1={80} y1={450} x2={80} y2={490} stroke={STROKE} strokeWidth={2} />
      <line x1={80} y1={490} x2={240} y2={490} stroke={STROKE} strokeWidth={2} />

      {/* No branch goes DOWN to merge point */}
      <line x1={240} y1={385} x2={240} y2={490} stroke={STROKE} strokeWidth={2} />

      {/* Merge → i = i + 1 */}
      <Arrow x1={240} y1={490} x2={240} y2={520} />
      <Rectangle cx={240} cy={535} label="i = i + 1" />

      {/* Loop-back: from i=i+1 bottom → down → far left → up → into outer diamond's left tip */}
      <line x1={240} y1={550} x2={240} y2={595} stroke={STROKE} strokeWidth={2} />
      <line x1={240} y1={595} x2={20} y2={595} stroke={STROKE} strokeWidth={2} />
      <line x1={20} y1={595} x2={20} y2={200} stroke={STROKE} strokeWidth={2} />
      <line x1={20} y1={200} x2={188} y2={200} stroke={STROKE} strokeWidth={2} markerEnd="url(#fc-arrow)" />
    </svg>
  );
}

// ─── Main widget ──────────────────────────────────────────────────────────────

export default function LU5FlowchartExercises() {
  const [activeId, setActiveId] = useState(1);
  const [completed, setCompleted] = useLocalStorageState<Record<string, boolean>>(STORAGE_KEY, {});

  const completedCount = Object.values(completed).filter(Boolean).length;

  const markComplete = (id: number) => {
    setCompleted({ ...completed, [String(id)]: true });
  };

  return (
    <div className="my-6 rounded-2xl border border-amber-900/20 bg-gradient-to-br from-amber-50 to-amber-100/40 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-amber-200 bg-amber-100/40">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <h3
            className="text-lg font-bold text-amber-950"
            style={{ fontFamily: "Palatino, serif" }}
          >
            Try it yourself — Flowchart exercises
          </h3>
          <div className="text-sm text-amber-900">
            <strong>{completedCount}</strong> of {EXERCISES.length} completed
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {EXERCISES.map((ex) => {
            const isActive = activeId === ex.id;
            const isDone = !!completed[String(ex.id)];
            return (
              <button
                key={ex.id}
                onClick={() => setActiveId(ex.id)}
                className={`px-3 py-1.5 rounded-md text-sm font-semibold transition flex items-center gap-1.5 ${
                  isActive
                    ? "bg-amber-800 text-amber-50"
                    : "bg-white text-amber-900 border border-amber-300 hover:bg-amber-50"
                }`}
              >
                <span>{ex.id} · {ex.short}</span>
                {isDone && (
                  <CheckCircle
                    className={`w-3.5 h-3.5 ${isActive ? "text-emerald-300" : "text-emerald-600"}`}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-5">
        {activeId === 1 && (
          <Exercise1Sequence done={!!completed["1"]} onComplete={() => markComplete(1)} />
        )}
        {activeId === 2 && (
          <Exercise2Selection done={!!completed["2"]} onComplete={() => markComplete(2)} />
        )}
        {activeId === 3 && (
          <Exercise3SpotError done={!!completed["3"]} onComplete={() => markComplete(3)} />
        )}
        {activeId === 4 && (
          <Exercise4FillBlanks done={!!completed["4"]} onComplete={() => markComplete(4)} />
        )}
        {activeId === 5 && (
          <Exercise5DrawYourself done={!!completed["5"]} onComplete={() => markComplete(5)} />
        )}
      </div>
    </div>
  );
}
